import { useQuery } from "@apollo/client";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import AddIcon from "@mui/icons-material/Add";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Typography, Box } from "@mui/material";
import type { DocumentNode } from "graphql";
import React, { useState, useEffect } from "react";
import { Field, type FieldRenderProps } from "react-final-form";
import { CrudCreateDialog } from "../CRUD/CrudCreateDialog";
import type { OptionType } from "./types";
import type { CreateSchemaDefinition } from "../CRUD/types";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

function optionValue(option: OptionType | string | number): string | number {
  return typeof option === "object" ? option.value : option;
}

function optionLabel(option: OptionType | string | number): string {
  return typeof option === "object" ? option.label : String(option);
}

// Компонент для автоматической очистки поля при disabled
function AutoClearWrapper({
  children,
  disabled,
  clearWhenDisabled,
  value,
  onChange,
  multiple,
  queryVariables,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  clearWhenDisabled?: boolean;
  value: any;
  onChange: (value: any) => void;
  multiple?: boolean;
  queryVariables?: Record<string, unknown>;
}) {
  const prevDisabled = React.useRef(disabled);
  const prevQueryVars = React.useRef(queryVariables);

  useEffect(() => {
    // Очищаем поле если оно стало disabled (но не было disabled раньше)
    if (disabled && !prevDisabled.current && clearWhenDisabled && value) {
      onChange(multiple ? [] : null);
    }
    prevDisabled.current = disabled;
  }, [disabled, clearWhenDisabled, value, onChange, multiple]);

  useEffect(() => {
    // Очищаем поле если изменились переменные запроса (например, сменили родительское поле)
    if (clearWhenDisabled && queryVariables && prevQueryVars.current && value) {
      const currentVarsStr = JSON.stringify(queryVariables);
      const prevVarsStr = JSON.stringify(prevQueryVars.current);

      if (currentVarsStr !== prevVarsStr) {
        onChange(multiple ? [] : null);
      }
    }
    prevQueryVars.current = queryVariables;
  }, [queryVariables, clearWhenDisabled, value, onChange, multiple]);

  return <>{children}</>;
}

interface AutocompleteFieldProps {
  name: string;
  queryVariables?: Record<string, unknown>;
  query?: DocumentNode;
  options?: OptionType[];
  label?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  helperText?: string;
  clearWhenDisabled?: boolean;
  // Новый объединенный create проп
  create?: {
    schema?: CreateSchemaDefinition;
    text?: string;
    getId?: (data: any) => string | number | null;
    initialVariables?: Record<string, any>;
  };
  optional?: boolean;
  nullable?: boolean;
  [key: string]: unknown;
}

export function AutocompleteField(props: AutocompleteFieldProps) {
  const { t } = useTranslation();
  const fieldQueryVariables: Record<string, unknown> = {};

  if (props.queryVariables) {
    Object.assign(fieldQueryVariables, props.queryVariables);
  }

  return (
    <Field
      {...props}
      allowNull
      parse={(value) => {
        if (!value || (props.multiple && !value.length)) {
          if (props.optional) return undefined;
        }
        return value || null;
      }}
      render={(fieldProps: FieldRenderProps<unknown, HTMLElement>) => {
        const { input } = fieldProps;
        const { onChange, value, onBlur, onFocus } = input;
        const { multiple, create } = props;

        // Используем create проп напрямую
        const finalCreateSchema = create?.schema;
        const finalCreateText =
          create?.text ||
          t("shared.form.autocompleteCreate", { defaultValue: "Добавить +" });
        const finalGetCreatedId = create?.getId;
        const finalCreateInitialVariables = create?.initialVariables;
        const {
          query,
          helperText,
          options = [],
          queryVariables = {},
          label,
          required,
          disabled,
          clearWhenDisabled,
          ...rest
        } = props;

        return (
          <AutoClearWrapper
            disabled={disabled}
            clearWhenDisabled={clearWhenDisabled}
            value={value}
            onChange={onChange}
            multiple={multiple}
            queryVariables={Object.assign(queryVariables, fieldQueryVariables)}
          >
            {query ? (
              <InputQuery
                query={query}
                queryVariables={Object.assign(
                  queryVariables,
                  fieldQueryVariables
                )}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                multiple={multiple}
                value={value}
                label={label}
                required={required}
                disabled={disabled}
                helperText={helperText}
                createSchema={finalCreateSchema}
                createText={finalCreateText}
                getCreatedId={finalGetCreatedId}
                createInitialVariables={finalCreateInitialVariables}
              />
            ) : (
              <Input
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                multiple={multiple}
                value={value}
                label={label}
                required={required}
                disabled={disabled}
                options={options}
                helperText={helperText}
                createSchema={finalCreateSchema}
                createText={finalCreateText}
                getCreatedId={finalGetCreatedId}
                createInitialVariables={finalCreateInitialVariables}
                {...rest}
              />
            )}
          </AutoClearWrapper>
        );
      }}
    />
  );
}

interface InputQueryProps {
  query: DocumentNode;
  value: unknown;
  queryVariables: Record<string, unknown>;
  onChange: (value: unknown) => void;
  onBlur: () => void;
  onFocus: () => void;
  multiple?: boolean;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  createSchema?: CreateSchemaDefinition;
  createText: string;
  getCreatedId?: (data: any) => string | number | null;
  createInitialVariables?: Record<string, any>;
}

function InputQuery({
  query,
  value,
  queryVariables,
  createSchema,
  createText,
  getCreatedId,
  createInitialVariables,
  ...props
}: InputQueryProps) {
  const [inputValue, setInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");

  // Синхронизируем inputValue с value - сбрасываем inputValue когда value становится пустым
  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setInputValue("");
    }
  }, [value]);

  const variables = {
    ...queryVariables,
    input: searchValue,
    value: [value].flat().filter(Boolean),
  };

  const { data, previousData, loading, refetch } = useQuery(query, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const onInputChange = (
    _: React.SyntheticEvent,
    input: string,
    reason: string
  ) => {
    if (!["selectOption", "reset"].includes(reason)) {
      setSearchValue(input);
    } else {
      setInputValue("");
    }
    setInputValue(input);
  };

  const result = data || previousData;

  let options: OptionType[] = [];

  if (result?.autocomplete?.options) {
    options = options.concat(result.autocomplete.options);
  }
  if (result?.autocomplete?.value) {
    options = options.concat(result.autocomplete.value);
  }

  return (
    <Input
      {...props}
      loading={loading}
      onInputChange={onInputChange}
      inputValue={inputValue}
      value={value}
      options={options}
      createSchema={createSchema}
      createText={createText}
      refetch={refetch}
      setInputValue={setInputValue}
      getCreatedId={getCreatedId}
      createInitialVariables={createInitialVariables}
      // Отключаем фильтрацию на фронтенде для query-автокомплита (фильтрация на бекенде)
      filterOptions={(options: any[]) => options}
    />
  );
}

interface InputProps {
  multiple?: boolean;
  value: unknown;
  loading?: boolean;
  options: OptionType[];
  disabled?: boolean;
  required?: boolean;
  onInputChange?: (
    event: React.SyntheticEvent,
    input: string,
    reason: string
  ) => void;
  onChange: (value: unknown) => void;
  onBlur: () => void;
  onFocus: () => void;
  label?: string;
  inputValue?: string;
  helperText?: string;
  createSchema?: CreateSchemaDefinition;
  createText: string;
  refetch?: () => Promise<any>;
  setInputValue?: (value: string) => void;
  getCreatedId?: (data: any) => string | number | null;
  createInitialVariables?: Record<string, any>;
  filterOptions?: (options: any[], state: any) => any[];
  [key: string]: unknown;
}

function Input({
  multiple,
  value,
  loading,
  options,
  disabled,
  required,
  onInputChange,
  onChange,
  onBlur,
  onFocus,
  label,
  inputValue,
  helperText,
  createSchema,
  createText,
  refetch,
  setInputValue,
  getCreatedId,
  createInitialVariables,
  filterOptions,
  ...rest
}: InputProps) {
  const { t } = useTranslation();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Специальная опция для "Добавить +"
  const createOption = {
    label: createText,
    value: "__CREATE_NEW__",
    isCreateOption: true,
  };

  // Добавляем опцию "Добавить +" если есть createSchema и поле не disabled
  const allOptions =
    createSchema && !disabled ? [...options, createOption] : options;

  // Кастомная фильтрация для статических опций
  const handleFilterOptions = (options: any[], state: any) => {
    // Если передана кастомная фильтрация (для query-автокомплита), используем её
    if (filterOptions) {
      return filterOptions(options, state);
    }

    // Для статических опций применяем стандартную фильтрацию MUI
    const filtered = options.filter((option: any) => {
      // Всегда показываем опцию создания
      if (option?.isCreateOption) {
        return true;
      }

      // Применяем стандартную фильтрацию для остальных опций
      const label = optionLabel(option).toLowerCase();
      const inputValue = state.inputValue.toLowerCase();
      return label.includes(inputValue);
    });

    return filtered;
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateSuccess = ({ data }: { data: any }) => {
    setCreateDialogOpen(false);

    // Рефетчим данные автокомплита для обновления списка опций
    if (refetch) {
      refetch()
        .then(() => {
          // Сбрасываем введенный текст поиска
          if (setInputValue) {
            setInputValue("");
          }

          // Извлекаем ID созданного объекта
          const createdId = getCreatedId
            ? getCreatedId(data)
            : data.created?.id;

          if (createdId) {
            if (multiple) {
              const currentValues = Array.isArray(value) ? value : [];
              onChange([...currentValues, createdId]);
            } else {
              onChange(createdId);
            }
          }

          enqueueSnackbar(
            t("shared.form.objectCreated", {
              defaultValue: "Объект успешно создан",
            }),
            { variant: "success" }
          );

          return handleAutocompleteClose();
        })
        .catch(() => {
          return handleAutocompleteClose();
        });
    }
  };

  const handleAutocompleteOpen = () => {
    setAutocompleteOpen(true);
    onFocus();
  };

  const handleAutocompleteClose = () => {
    setAutocompleteOpen(false);
    onBlur();
  };

  const handleOptionSelect = (_: any, newValue: any) => {
    // Проверяем, выбрана ли опция "Добавить +"
    if (newValue && newValue.value === "__CREATE_NEW__") {
      // Закрываем автокомплит с небольшой задержкой для корректной работы
      setTimeout(() => {
        setAutocompleteOpen(false);
      }, 0);
      setCreateDialogOpen(true);
      return;
    }

    // Обычная обработка выбора опции
    if (multiple) {
      if (!newValue) return onChange([]);
      if (Array.isArray(newValue)) {
        // Фильтруем опции создания из выбранных значений
        const filteredValues = newValue.filter(
          (v: any) => !v.isCreateOption && v.value !== "__CREATE_NEW__"
        );
        return onChange(filteredValues.map((v: any) => optionValue(v)));
      }
      return onChange([optionValue(newValue)]);
    } else {
      if (!newValue) return onChange(null);
      if (Array.isArray(newValue)) {
        return onChange(optionValue(newValue[0]));
      }
      return onChange(optionValue(newValue));
    }
  };

  return (
    <>
      <Autocomplete
        getOptionLabel={(option: any) => {
          if (option?.isCreateOption) {
            return option.label;
          }
          return typeof option === "object"
            ? optionLabel(option)
            : options.find((o) => optionValue(o) === option)?.label ||
                optionLabel(option);
        }}
        getOptionKey={(option: any) => {
          if (option?.isCreateOption) {
            return "__CREATE_NEW__";
          }
          return optionValue(option);
        }}
        renderOption={(props, option: any) => {
          if (option?.isCreateOption) {
            return (
              <ListItem
                {...props}
                key="__CREATE_NEW__"
                disablePadding
                sx={{
                  borderTop: 1,
                  borderColor: "divider",
                  color: "primary.main",
                  fontWeight: "medium",
                }}
              >
                <AddIcon sx={{ mr: 1, fontSize: 20 }} />
                <ListItemText primary={option.label} />
              </ListItem>
            );
          }
          return (
            <ListItem disablePadding {...props} key={optionValue(option)}>
              <ListItemText
                primary={optionLabel(option)}
                secondary={option?.subtitle}
                slotProps={
                  option?.subtitle
                    ? {
                        secondary: {
                          variant: "caption",
                          color: "text.secondary",
                        },
                      }
                    : undefined
                }
              />
            </ListItem>
          );
        }}
        openOnFocus
        clearOnBlur={false}
        handleHomeEndKeys
        multiple={multiple}
        loading={loading}
        options={allOptions}
        disabled={disabled}
        value={value ? value : multiple ? [] : null}
        open={autocompleteOpen}
        onOpen={handleAutocompleteOpen}
        onClose={handleAutocompleteClose}
        inputValue={inputValue}
        onInputChange={onInputChange}
        filterOptions={handleFilterOptions}
        isOptionEqualToValue={(option: any, value: any) => {
          if (option?.isCreateOption) {
            return false;
          }
          return optionValue(option) === value;
        }}
        onChange={handleOptionSelect}
        slotProps={{
          popper: {
            sx: {
              zIndex: 1400,
            },
          },
        }}
        disablePortal={false}
        renderInput={(params) => {
          // Находим выбранные опции для отображения subtitle
          let subtitles: string[] = [];

          if (!multiple && value) {
            const selectedOption = options.find(
              (o) => optionValue(o) === value
            );
            if (selectedOption?.subtitle) {
              subtitles = [selectedOption.subtitle];
            }
          } else if (multiple && value && Array.isArray(value)) {
            subtitles = value
              .map(
                (val) => options.find((o) => optionValue(o) === val)?.subtitle
              )
              .filter((subtitle): subtitle is string => Boolean(subtitle));
          }

          const subtitleText =
            subtitles.length > 0 ? subtitles.join(", ") : null;

          return (
            <Box sx={{ position: "relative" }}>
              <TextField
                {...params}
                label={label}
                fullWidth
                helperText={helperText}
                required={required ? !multiple : false}
                slotProps={{
                  inputLabel: {
                    required,
                  },
                }}
              />
              {!inputValue && subtitleText && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    position: "absolute",
                    bottom: "50%",
                    left: 16,
                    top: 40,
                    fontSize: "0.7rem",
                    pointerEvents: "none",
                    lineHeight: 1,
                    zIndex: 1,
                  }}
                >
                  {subtitleText}
                </Typography>
              )}
            </Box>
          );
        }}
        {...rest}
      />

      {createSchema && (
        <CrudCreateDialog
          open={createDialogOpen}
          onClose={handleCreateDialogClose}
          onSubmitSuccess={handleCreateSuccess}
          createSchema={createSchema}
          texts={{ createItem: createText.replace(" +", "") }}
          refetchQueries={[]}
          fullScreen={isMobile}
          maxWidth={isMobile ? false : "md"}
          initialVariables={createInitialVariables}
        />
      )}
    </>
  );
}
